import { useMemo } from 'react';
import { useRouter } from 'next/router';

import { formatCategoryList, useInitLikedProducts, useLikeProductService } from '@widgets/like-product';
import { useReviewListConfig } from '@domains/review-list-common';

import { type AdReviewRecommendGroup_ad_review_recommend_group_UxGoodsGroup_goods_carousel_component_list as ProductCardItem } from '../graphql/ad_review_recommend_group_types.2';
import { useAdReivewLog } from '../log/useAdReviewLog';
import { useAdReviewRecommendGroup } from '../query/useAdReviewRecommendGroup';

const convertList = (list: ProductCardItem[]) => list.map(({ catalog_product_id }) => catalog_product_id || '');

export const useAdreviewRecommendGroupService = () => {
  const { path, isZigzagApp, catalog_product_id } = useReviewListConfig();
  const { data } = useAdReviewRecommendGroup(catalog_product_id);
  const is_ad = data?.[0]?.is_ad ?? false;
  const main_title = data?.[0]?.main_title;
  const { impressionProductItem, clickProductItem, savedProduct } = useAdReivewLog(catalog_product_id);
  const { onLikeProduct } = useLikeProductService();
  const { push } = useRouter();

  const item_list = useMemo(() => data?.[0]?.goods_carousel?.component_list ?? [], [data]);
  useInitLikedProducts({
    data: item_list,
    convert: convertList,
  });

  const onImpressionProduct = (
    { catalog_product_id, log }: Pick<ProductCardItem, 'log' | 'catalog_product_id'>,
    index: number,
  ) =>
    impressionProductItem({
      title: main_title?.text ?? '',
      log: log ?? '',
      productId: catalog_product_id || '',
      index,
    });

  const onClickProduct = (item: ProductCardItem, index: number) => {
    clickProductItem({
      title: main_title?.text ?? '',
      position: item.position,
      productId: item.catalog_product_id || '',
      log: item.log ?? '',
      index,
    });

    if (item.catalog_product_id && path.product) {
      if (isZigzagApp) {
        path.product.detail({
          browsing_type: item.browsing_type,
          product_url: item.product_url ?? '',
          shop_id: item.shop_id,
          shop_product_no: item.shop_product_no ?? '',
          catalog_product_id: item.catalog_product_id,
        });
        return;
      }
      push(
        path.product.detail({
          browsing_type: item.browsing_type,
          product_url: item.product_url ?? '',
          shop_id: item.shop_id,
          shop_product_no: item.shop_product_no ?? '',
          catalog_product_id: item.catalog_product_id,
        }),
      );
    }
  };

  const onSavedProduct = (checked: boolean, item: ProductCardItem, index: number) => {
    const inAppLikeProductLog = {
      category: checked ? 'save_product' : 'remove_saved_product',
      navigation: 'product_detail',
      object_idx: 0,
      object_section: 'ad_review_carousel',
      object_type: 'catalog',
      object_id: item.catalog_product_id,
      data: JSON.stringify({
        original_product_id: catalog_product_id,
        item_idx: item.position,
        server_log: item.log,
        title: main_title?.text ?? '',
      }),
    };

    const inAppLikeProductPayload = {
      like: checked,
      browsingType: item.browsing_type,
      shopId: Number(item.shop_id),
      shopProductNo: item.shop_product_no,
      catalogProductId: item.catalog_product_id,
      title: item.title || '',
      goodsUrl: item.product_url || '',
      imageUrl: item.image_url || '',
      price: Number(item.price),
      discountRate: Number(item.discount_rate),
      isZpay: Boolean(item.zpay),
      isFreeShipping: Boolean(item.free_shipping),
      log: inAppLikeProductLog,
    };

    const airbridge = {
      brandID: item.shop_id,
      brandName: item.shop_name,
      productID: item.catalog_product_id,
      productName: item.title,
      price: item.final_price,
      categoryID: formatCategoryList(item.managed_category_list, 'id'),
      categoryName: formatCategoryList(item.managed_category_list, 'value'),
    };

    const webLikeProductPayload = {
      checked,
      productData: { catalog_product_id: item.catalog_product_id || '', shop_id: item.shop_id || '' },
      airbridge,
      sendLog: (checked: boolean) =>
        savedProduct({
          title: main_title?.text ?? '',
          log: item.log ?? '',
          category: checked ? 'save_product' : 'remove_saved_product',
          productId: item.catalog_product_id || '',
          index,
        }),
    };

    onLikeProduct({ inAppLikeProductPayload, webLikeProductPayload });
  };

  return { item_list, is_ad, main_title, onImpressionProduct, onClickProduct, onSavedProduct };
};
